import { worldToLocal } from "@/modes/alignment-modes-commons/alignment-transform";
import { selectOuterRoomSectionAncestor } from "@/modes/mode-selectors";
import { store } from "@/store/store";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { assert } from "@faro-lotv/foundation";
import { IElement, IPose } from "@faro-lotv/ielement-types";
import {
  selectIElement,
  selectIElementProjectApiLocalPose,
} from "@faro-lotv/project-source";

import { Vector3 } from "three";

type LocalPoseForMovedWaypoint = {
  /** The element that should be moved. */
  elementToMove: IElement;

  /** The pose to apply to the element that should be moved. */
  localPose: IPose;
};

/**
 * Calculates which element should be moved to which pose, to achieve a desired target position for a waypoint.
 * In the UI, the user is not actually dragging room sections, but rather placeholders corresponding to individual time points.
 * This function determines where, and how we want to persist these move operations.
 *
 * @param element An element that should be moved. Can also be any child of a room section.
 * @param newPosition The new position for the passed element in three.js world space.
 * @returns An element to move and its local pose.
 */
export function calculateLocalPoseForMovedWaypoint(
  element: IElement,
  newPosition: Vector3,
): LocalPoseForMovedWaypoint {
  const elementParent = selectIElement(element.parentId)(store.getState());
  assert(elementParent, "Expected to find parent for waypoint move");

  const roomToMove = selectOuterRoomSectionAncestor(element)(store.getState());

  const roomToMoveParent = selectIElement(roomToMove?.parentId)(
    store.getState(),
  );

  assert(
    roomToMove && roomToMoveParent,
    "Expected to find a room to move for the selected element",
  );

  const roomToMoveParentMatrix = selectIElementWorldMatrix4(
    roomToMoveParent.id,
  )(store.getState());
  const roomToMoveMatrix = selectIElementWorldMatrix4(roomToMove.id)(
    store.getState(),
  ).clone();

  const roomPrevPosition = new Vector3().setFromMatrixPosition(
    roomToMoveMatrix,
  );

  roomToMoveMatrix.setPosition(
    // We want to keep the original height, because the position of the waypoint has been projected into the sheet height during editing
    new Vector3().fromArray([newPosition.x, roomPrevPosition.y, newPosition.z]),
  );

  const roomToMoveMatrixLocal = worldToLocal(
    roomToMoveParentMatrix,
    roomToMoveMatrix,
  );

  const localPose = selectIElementProjectApiLocalPose(
    roomToMove,
    roomToMoveMatrixLocal,
  )(store.getState());

  const previousPose = roomToMove.pose ?? {
    gps: null,
    pos: null,
    scale: null,
    isWorldRot: false,
    rot: null,
  };

  assert(
    !previousPose.isWorldPose,
    "Expected previous pose to be a local pose",
  );

  return {
    elementToMove: roomToMove,
    localPose: {
      ...previousPose,
      ...localPose,
    },
  };
}
